// Store module for Package (Formule Combinée) creation/editing
// Manages state between steps to minimize DB queries

const state = () => ({
    // Current step tracking
    currentStep: 1,

    // Package ID (null for create, number for edit)
    packageId: null,

    // Step 1 - Type d'expérience
    selectedType: 'specificPrestation', // 'specificPrestation', 'family', 'allPrestations'

    // Step 2 - Informations générales
    formData: {
        name: '',
        internal_name: '',
        description: '',
        image: null,
        imagePreview: '',
        visible_online: true,
        visible_online_pos: true,
    },

    // Step 3 - Choix de la formule (combo items)
    comboItems: [],

    // Step 4 - Tarification
    pricing: {
        price: 0,
        price_ht: 0,
        vat_rate: 20,
        // 'group' (forfait) ou 'per_person' (prix x nombre de participants)
        pricing_type: 'group',
        code_ean: '',
        is_by_percent: false,
        remise_percent: null,
    },

    // Step 4 - Acompte
    deposite: {
        enabled: false,
        type: null, // 'percentage' or 'amount'
        value: null,
    },

    // Step 5 - Paramètres
    parameters: {
        item_qty_min: null,
        item_qty_max: null,
        slot_succession: 0, // délai entre activités
        is_same_time: false, // plannings identiques
        is_order_required: false, // ordre obligatoire
        is_multi_participants: false, // participants différents par élément
        questions_are_merged: false,
    },

    // Tag
    selectedTag: null,

    // Available data from backend
    availableActivities: [],
    availableTags: [],
    countryVatRates: [],

    // Loading and error states
    loading: false,
    errors: {},

    // Track if data has been modified
    isDirty: false,
})

const mutations = {
    SET_CURRENT_STEP(state, step) {
        state.currentStep = step
    },

    SET_PACKAGE_ID(state, id) {
        state.packageId = id
    },

    SET_SELECTED_TYPE(state, type) {
        state.selectedType = type
        state.isDirty = true
    },

    SET_FORM_DATA(state, data) {
        state.formData = { ...state.formData, ...data }
        state.isDirty = true
    },

    SET_COMBO_ITEMS(state, items) {
        state.comboItems = items
        state.isDirty = true
    },

    ADD_COMBO_ITEM(state, item) {
        state.comboItems.push({
            selectedActivity: null,
            selectedDuration: null,
            isRequired: false,
            isTicketAsOption: false,
            ticketAsOption: null,
            ...item,
        })
        state.isDirty = true
    },

    UPDATE_COMBO_ITEM(state, { index, data }) {
        if (state.comboItems[index]) {
            // Use splice to ensure reactivity in Vue 2
            state.comboItems.splice(index, 1, { ...state.comboItems[index], ...data })
            state.isDirty = true
        }
    },

    REMOVE_COMBO_ITEM(state, index) {
        state.comboItems.splice(index, 1)
        state.isDirty = true
    },

    SET_PRICING(state, data) {
        state.pricing = { ...state.pricing, ...data }
        state.isDirty = true
    },

    SET_DEPOSITE(state, data) {
        state.deposite = { ...state.deposite, ...data }
        state.isDirty = true
    },

    SET_PARAMETERS(state, data) {
        state.parameters = { ...state.parameters, ...data }
        state.isDirty = true
    },

    SET_SELECTED_TAG(state, tag) {
        state.selectedTag = tag
        state.isDirty = true
    },

    SET_AVAILABLE_ACTIVITIES(state, activities) {
        state.availableActivities = activities
    },

    SET_AVAILABLE_TAGS(state, tags) {
        state.availableTags = tags
    },

    SET_COUNTRY_VAT_RATES(state, rates) {
        state.countryVatRates = rates
    },

    SET_LOADING(state, loading) {
        state.loading = loading
    },

    SET_ERRORS(state, errors) {
        state.errors = errors
    },

    CLEAR_ERROR(state, field) {
        if (state.errors[field]) {
            delete state.errors[field]
        }
    },

    SET_IS_DIRTY(state, isDirty) {
        state.isDirty = isDirty
    },

    // Load package data from API for edit mode
    LOAD_PACKAGE(state, packageData) {
        state.packageId = packageData.id
        state.selectedType = packageData.type || 'specificPrestation'

        state.formData = {
            name: packageData.name || '',
            internal_name: packageData.internal_name || '',
            description: packageData.description || '',
            image: null,
            imagePreview: packageData.image || '',
            visible_online: packageData.visible_online == 1,
            visible_online_pos: packageData.visible_online_pos == 1,
        }

        state.pricing = {
            price: packageData.price || 0,
            price_ht: packageData.price_ht || 0,
            vat_rate: parseFloat(packageData.vat_rate) || 20,
            pricing_type: packageData.pricing_type || 'group',
            code_ean: packageData.code_ean || '',
            is_by_percent: packageData.remise_percent !== null,
            remise_percent: packageData.remise_percent,
        }

        state.deposite = {
            enabled: packageData.deposite == 1,
            type: packageData.deposite_type,
            value: packageData.deposite_value,
        }

        state.parameters = {
            item_qty_min: packageData.item_qty_min,
            item_qty_max: packageData.item_qty_max,
            slot_succession: packageData.slot_succession || 0,
            is_same_time: packageData.is_same_time == 1 || packageData.is_same_time === true,
            is_order_required: packageData.is_order_required == 1 || packageData.is_order_required === true,
            is_multi_participants: packageData.is_multi_participants == 1 || packageData.is_multi_participants === true,
            questions_are_merged: packageData.questions_are_merged == 1 || packageData.questions_are_merged === true,
        }

        // Load combo items if present
        if (packageData.items && packageData.items.length > 0) {
            state.comboItems = packageData.items.map((item) => {
                const activityId =
                    item.prestation_id || item.prestation?.id || item.activityId || item.itemable?.id || item.item_id
                const foundActivity = state.availableActivities.find((a) => a.id == activityId)

                // Find the matching duration from the activity's prices_by_times array
                // This is important for vue-select to properly match the selected value with options (reference equality)
                let selectedDuration = null
                if (foundActivity) {
                    const durations =
                        foundActivity.prices_by_times ||
                        foundActivity.pricesByTimes ||
                        foundActivity.durations ||
                        foundActivity.prices ||
                        []
                    // Match by duration_id first, then by duration value as fallback
                    selectedDuration =
                        durations.find((d) => d.id == item.duration_id) ||
                        durations.find((d) => d.duration == item.duration) ||
                        null
                }

                // Fallback to creating a minimal object if no match found (for backward compatibility)
                if (!selectedDuration && (item.duration_id || item.duration)) {
                    selectedDuration = {
                        id: item.duration_id,
                        duration: item.duration,
                    }
                }

                return {
                    selectedActivity: foundActivity || item.prestation || item.itemable || null,
                    selectedDuration: selectedDuration,
                    isRequired: item.is_required == true,
                    isTicketAsOption: item.prestation_option_id !== null,
                    ticketAsOption: item.prestation_option || null,
                }
            })
        }

        // Load tag
        if (packageData.tag) {
            state.selectedTag = packageData.tag
        } else if (packageData.tag_id) {
            state.selectedTag = state.availableTags.find((t) => t.id == packageData.tag_id) || null
        } else {
            state.selectedTag = null
        }

        state.isDirty = false
    },

    RESET_STORE(state) {
        state.currentStep = 1
        state.packageId = null
        state.selectedType = 'specificPrestation'
        state.formData = {
            name: '',
            internal_name: '',
            description: '',
            image: null,
            imagePreview: '',
            visible_online: true,
            visible_online_pos: true,
        }
        state.comboItems = []
        state.pricing = {
            price: 0,
            price_ht: 0,
            vat_rate: 20,
            code_ean: '',
            is_by_percent: false,
            remise_percent: null,
        }
        state.deposite = {
            enabled: false,
            type: null,
            value: null,
        }
        state.parameters = {
            item_qty_min: null,
            item_qty_max: null,
            slot_succession: 0,
            is_same_time: false,
            is_order_required: false,
            is_multi_participants: false,
            questions_are_merged: false,
        }
        state.selectedTag = null
        state.errors = {}
        state.isDirty = false
    },
}

const actions = {
    setCurrentStep({ commit }, step) {
        commit('SET_CURRENT_STEP', step)
    },

    setSelectedType({ commit }, type) {
        commit('SET_SELECTED_TYPE', type)
    },

    setFormData({ commit }, data) {
        commit('SET_FORM_DATA', data)
    },

    setComboItems({ commit }, items) {
        commit('SET_COMBO_ITEMS', items)
    },

    addComboItem({ commit }, item = {}) {
        commit('ADD_COMBO_ITEM', item)
    },

    updateComboItem({ commit }, { index, data }) {
        commit('UPDATE_COMBO_ITEM', { index, data })
    },

    removeComboItem({ commit }, index) {
        commit('REMOVE_COMBO_ITEM', index)
    },

    setPricing({ commit }, data) {
        commit('SET_PRICING', data)
    },

    setDeposite({ commit }, data) {
        commit('SET_DEPOSITE', data)
    },

    setParameters({ commit }, data) {
        commit('SET_PARAMETERS', data)
    },

    setSelectedTag({ commit }, tag) {
        commit('SET_SELECTED_TAG', tag)
    },

    setAvailableActivities({ commit }, activities) {
        commit('SET_AVAILABLE_ACTIVITIES', activities)
    },

    setAvailableTags({ commit }, tags) {
        commit('SET_AVAILABLE_TAGS', tags)
    },

    setCountryVatRates({ commit }, rates) {
        commit('SET_COUNTRY_VAT_RATES', rates)
    },

    setErrors({ commit }, errors) {
        commit('SET_ERRORS', errors)
    },

    clearError({ commit }, field) {
        commit('CLEAR_ERROR', field)
    },

    // Load package for edit mode
    async loadPackage({ commit }, packageId) {
        commit('SET_LOADING', true)
        try {
            const response = await axios.get(`/api/v3/package/${packageId}`)
            commit('LOAD_PACKAGE', response.data.data)
            return response.data.data
        } catch (error) {
            console.error('Error loading package:', error)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Save package (create or update)
    async savePackage({ state, commit }) {
        commit('SET_LOADING', true)
        commit('SET_ERRORS', {})

        try {
            const payload = {
                id: state.packageId,
                type: state.selectedType,
                name: state.formData.name,
                internal_name: state.formData.internal_name || null,
                description: state.formData.description,
                image: state.formData.image,
                visible_online: state.formData.visible_online,
                visible_online_pos: state.formData.visible_online_pos,
                price: state.pricing.price,
                price_ht: state.pricing.price_ht,
                vat_rate: state.pricing.vat_rate,
                pricing_type: state.pricing.pricing_type || 'group',
                code_ean: state.pricing.code_ean,
                remise_percent: state.pricing.is_by_percent ? state.pricing.remise_percent : null,
                deposite: state.deposite.enabled ? 1 : 0,
                deposite_type: state.deposite.type,
                deposite_value: state.deposite.value,
                item_qty_min: state.parameters.item_qty_min,
                item_qty_max: state.parameters.item_qty_max,
                slot_succession: state.parameters.slot_succession,
                is_same_time: state.parameters.is_same_time ? 1 : 0,
                is_order_required: state.parameters.is_order_required ? 1 : 0,
                is_multi_participants: state.parameters.is_multi_participants ? 1 : 0,
                questions_are_merged: state.parameters.questions_are_merged ? 1 : 0,
                steps: state.currentStep, // Save current step for resuming
                comboItems: state.comboItems.map((item, index) => ({
                    activityId: item.selectedActivity?.id,
                    duration: item.selectedDuration?.duration,
                    durationId: item.selectedDuration?.id,
                    isRequired: item.isRequired,
                    isTicketAsOption: item.isTicketAsOption,
                    ticketAsOption: item.ticketAsOption,
                    sort_order: index,
                })),
                // selected_tags: state.selectedTag ? [state.selectedTag] : [],
                tag_id: state.selectedTag?.id,
            }

            const url = state.packageId ? `/api/v3/package/${state.packageId}` : '/api/v3/package'
            const method = state.packageId ? 'put' : 'post'

            const response = await axios[method](url, payload)
            commit('SET_IS_DIRTY', false)

            if (!state.packageId && response.data.data?.id) {
                commit('SET_PACKAGE_ID', response.data.data.id)
            }

            return response.data
        } catch (error) {
            if (error.response?.data?.errors) {
                commit('SET_ERRORS', error.response.data.errors)
            }
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Save step progress to database (for resumable creation)
    async saveStepProgress({ state, commit, dispatch }, stepNumber) {
        commit('SET_CURRENT_STEP', stepNumber)

        // Only save if we have at least step 1 completed (type selected)
        if (!state.selectedType) {
            return
        }

        commit('SET_LOADING', true)
        try {
            const payload = {
                id: state.packageId,
                type: state.selectedType,
                name: state.formData.name || 'Brouillon - Formule',
                internal_name: state.formData.internal_name || null,
                description: state.formData.description,
                image: state.formData.image,
                visible_online: 0, // Not visible until fully created
                visible_online_pos: 1, // visible pos until fully created
                // Compute isByPercent based on type and is_multi_participants
                // eslint-disable-next-line no-unused-vars
                ...(() => {
                    const isByPercent = state.selectedType === 'family' && state.parameters.is_multi_participants == true
                    if (isByPercent) {
                        return {
                            price: 0,
                            price_ht: 0,
                            vat_rate: null,
                            pricing_type: 'group',
                            remise_percent: state.pricing.remise_percent,
                        }
                    }
                    return {
                        price: state.pricing.price || 0,
                        price_ht: state.pricing.price_ht || 0,
                        vat_rate: state.pricing.vat_rate != null ? state.pricing.vat_rate : 20,
                        pricing_type: state.pricing.pricing_type || 'group',
                        remise_percent: null,
                    }
                })(),
                code_ean: state.pricing.code_ean,
                deposite: state.deposite.enabled ? 1 : 0,
                deposite_type: state.deposite.type,
                deposite_value: state.deposite.value,
                item_qty_min: state.parameters.item_qty_min,
                item_qty_max: state.parameters.item_qty_max,
                slot_succession: state.parameters.slot_succession,
                is_same_time: state.parameters.is_same_time ? 1 : 0,
                is_order_required: state.parameters.is_order_required ? 1 : 0,
                is_multi_participants: state.parameters.is_multi_participants ? 1 : 0,
                questions_are_merged: state.parameters.questions_are_merged ? 1 : 0,
                steps: stepNumber, // Current step for resuming later
                is_active: 0, // Not active until fully created
                book_online: 0,
                comboItems: state.comboItems
                    .filter((item) => item.selectedActivity)
                    .map((item, index) => ({
                        activityId: item.selectedActivity?.id,
                        duration: item.selectedDuration?.duration,
                        durationId: item.selectedDuration?.id,
                        isRequired: item.isRequired,
                        isTicketAsOption: item.isTicketAsOption,
                        ticketAsOption: item.ticketAsOption,
                        sort_order: index,
                    })),
                // selected_tags: state.selectedTag ? [state.selectedTag] : [],
                tag_id: state.selectedTag?.id,
            }

            const url = state.packageId ? `/api/v3/package/${state.packageId}` : '/api/v3/package'
            const method = state.packageId ? 'put' : 'post'

            const response = await axios[method](url, payload)
            commit('SET_IS_DIRTY', false)

            // Store the package ID for subsequent saves
            if (!state.packageId && response.data.data?.id) {
                commit('SET_PACKAGE_ID', response.data.data.id)
            }

            return response.data
        } catch (error) {
            console.error('Error saving step progress:', error)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    resetStore({ commit }) {
        commit('RESET_STORE')
    },

    updateParameterWithSideEffects({ commit, state }, { key, value }) {
        // Update the parameter
        const newParameters = { ...state.parameters, [key]: value }
        commit('SET_PARAMETERS', newParameters)

        // Handle Side Effects matches legacy AddUpdatePackage.vue logic
        const sideEffectKeys = ['is_same_time', 'is_multi_participants', 'is_order_required']

        if (sideEffectKeys.includes(key)) {
            // Reset discount if structure changes
            if (key !== 'is_order_required') {
                commit('SET_PRICING', { ...state.pricing, remise_percent: null })
            }

            // Handle is_by_percent logic
            // Legacy: this.formData.is_by_percent = this.formData.is_multi_participants
            let isByPercent = state.pricing.is_by_percent

            if (key === 'is_multi_participants') {
                isByPercent = value
                commit('SET_PRICING', { ...state.pricing, is_by_percent: isByPercent })

                // Automatic Type Switching Logic
                // If enabled -> Family (allows multi-participants)
                // If disabled -> Specific (default custom combo)
                if (value) {
                    commit('SET_SELECTED_TYPE', 'family')
                } else {
                    commit('SET_SELECTED_TYPE', 'specificPrestation')
                }
            }

            // Logic from handleComboIsPercent
            if (isByPercent) {
                commit('SET_PRICING', {
                    ...state.pricing,
                    is_by_percent: true,
                    price: 0,
                    price_ht: 0,
                    vat_rate: 0,
                })
            }
        }
    },
}

const getters = {
    currentStep: (state) => state.currentStep,
    packageId: (state) => state.packageId,
    isEditMode: (state) => state.packageId !== null,
    selectedType: (state) => state.selectedType,
    formData: (state) => state.formData,
    comboItems: (state) => state.comboItems,
    pricing: (state) => state.pricing,
    deposite: (state) => state.deposite,
    parameters: (state) => state.parameters,
    selectedTag: (state) => state.selectedTag,
    availableActivities: (state) => state.availableActivities,
    availableTags: (state) => state.availableTags,
    countryVatRates: (state) => state.countryVatRates,
    loading: (state) => state.loading,
    errors: (state) => state.errors,
    isDirty: (state) => state.isDirty,

    // Computed isByPercent based on type and is_multi_participants
    // When type == 'family' AND is_multi_participants == true, pricing is by percentage discount
    isByPercent: (state) => {
        return state.selectedType === 'family' && state.parameters.is_multi_participants == true
    },

    // Computed total price from combo items
    totalComboPrice: (state) => {
        return state.comboItems.reduce((total, item) => {
            const price = item.selectedDuration?.price || 0
            return total + parseFloat(price)
        }, 0)
    },

    // Check if all durations are the same
    areDurationsSame: (state) => {
        if (state.comboItems.length < 2) return true
        const firstDuration = state.comboItems[0]?.selectedDuration?.duration
        return state.comboItems.every((item) => item.selectedDuration?.duration == firstDuration)
    },

    // Get available activities excluding already selected ones
    getAvailableActivities: (state) => (currentIndex) => {
        const selectedIds = state.comboItems
            .filter((_, index) => index !== currentIndex)
            .map((item) => item.selectedActivity?.id)
            .filter((id) => id !== undefined)

        let activities = state.availableActivities.filter((activity) => !selectedIds.includes(activity.id))
        const isOnlyVR = state.parameters.is_same_time === true || state.parameters.is_same_time == 1

        if (isOnlyVR) {
            activities = activities.filter((activity) => activity.subcategory?.slug === 'vr')
        }

        // Filter based on is_same_time constraint (only for items after the first one)
        if (isOnlyVR && currentIndex > 0) {
            const firstItem = state.comboItems[0]
            const targetDuration = firstItem?.selectedDuration?.duration
            const targetSlotConfig = firstItem?.selectedActivity?.slot_config

            if (targetDuration && targetSlotConfig) {
                activities = activities.filter((activity) => {
                    // Check slot_config
                    if (activity.slot_config != targetSlotConfig) return false

                    // Check duration availability
                    const durations =
                        activity.prices_by_times || activity.pricesByTimes || activity.durations || activity.prices || []
                    return durations.some((d) => d.duration == targetDuration)
                })
            }
        }

        return activities
    },

    // Validation state for each step
    isStep1Valid: (state) => {
        return state.selectedType !== null && state.selectedType !== ''
    },

    isStep2Valid: (state) => {
        return state.formData.name && state.formData.name.trim() !== ''
    },

    isStep3Valid: (state) => {
        if (state.selectedType === 'allPrestations') return true
        return state.comboItems.length > 0 && state.comboItems.every((item) => item.selectedActivity && item.selectedDuration)
    },

    isStep4Valid: (state) => {
        // Compute isByPercent based on type and is_multi_participants (same logic as isByPercent getter)
        const isByPercent = state.selectedType === 'family' && state.parameters.is_multi_participants == true

        if (isByPercent) {
            // In percent mode, remise is optional but if enabled, must be valid
            // For now, always return true since remise is optional
            return true
        }
        // In fixed price mode, price and vat are optional (user can skip if not enabling specific price)
        return true
    },

    isStep5Valid: (state) => {
        return state.parameters.is_same_time !== null
    },

    // Get step validation status
    getStepValidation: (state, getters) => (stepId) => {
        switch (stepId) {
            case 1:
                return getters.isStep1Valid
            case 2:
                return getters.isStep2Valid
            case 3:
                return getters.isStep3Valid
            case 4:
                return getters.isStep4Valid
            case 5:
                return getters.isStep5Valid
            case 6:
                return true // Options step
            default:
                return false
        }
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
